"use client";

import * as React from "react";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsBadge() {
  return (
    <>
      {/* Page Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Badge
            </Text>
            <Badge variant="secondary">Display</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            Small status indicators and labels for categorization, counts, or
            highlighting information.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/badge" />
        </SectionCard>
      </Section>

      {/* Variants */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Variants</CardTitle>
              <CardDescription>Available badge styles</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex flex-wrap gap-4">
            <Badge variant="default">Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge variant="destructive">Destructive</Badge>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{`<Badge variant="default">Default</Badge>`}</code>
            <code className="block">{`<Badge variant="secondary">Secondary</Badge>`}</code>
            <code className="block">{`<Badge variant="outline">Outline</Badge>`}</code>
            <code className="block">{`<Badge variant="destructive">Destructive</Badge>`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Use Cases */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Common Use Cases</CardTitle>
              <CardDescription>How badges are typically used</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-6"
        >
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <Text as="p" className="font-medium">Status indicators</Text>
              <div className="flex gap-2">
                <Badge className="bg-success-bg text-success border-success/20">Active</Badge>
                <Badge className="bg-warning-bg text-warning border-warning/20">Pending</Badge>
                <Badge className="bg-error-bg text-destructive border-destructive/20">Inactive</Badge>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Text as="p" className="font-medium">Categories/Tags</Text>
              <div className="flex gap-2">
                <Badge variant="outline">React</Badge>
                <Badge variant="outline">TypeScript</Badge>
                <Badge variant="outline">Tailwind</Badge>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Text as="p" className="font-medium">Counts/Numbers</Text>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Text as="p">Notifications</Text>
                  <Badge>5</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Text as="p">Messages</Text>
                  <Badge variant="destructive">99+</Badge>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Text as="p" className="font-medium">Feature flags</Text>
              <div className="flex gap-2">
                <Badge variant="default">New</Badge>
                <Badge variant="secondary">Beta</Badge>
                <Badge variant="outline">Coming Soon</Badge>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* With Other Components */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Composition</CardTitle>
              <CardDescription>Using badges with other components</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Text as="h3" className="text-h4 font-semibold">Page Title</Text>
              <Badge variant="secondary">Draft</Badge>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex flex-col gap-1">
                <Text as="p" className="font-medium">Premium Plan</Text>
                <Text as="p" className="text-sm text-muted-foreground">Full access to all features</Text>
              </div>
              <Badge>Recommended</Badge>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Import</CardTitle>
              <CardDescription>How to import the component</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code>{`import { Badge } from "@/registry/new-york/ui/badge";`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
          header={<CardTitle>Props</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Prop</th>
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Default</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">variant</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">&quot;default&quot; | &quot;secondary&quot; | &quot;outline&quot; | &quot;destructive&quot;</td>
                <td className="py-2 font-mono text-xs">&quot;default&quot;</td>
                <td className="py-2 text-muted-foreground">Visual style variant</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">children</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Badge content</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">className</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Additional CSS classes</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>
    </>
  );
}
